import * as React from 'react';
import { Link } from 'react-router';
import { __ } from 'react-i18n';
import styled from 'styled-components';
import { rem } from 'polished';
import {
  FlexCol,
  FlexRowCenter,
  FlexColCenterHorizontal,
} from '../../../Defaults/Flexbox/Flexbox';
import { ButtonSecondary } from '../../General/Button';
import { SummaryValue } from './SummaryValue';
import { AcceptTermsCheckbox } from './AcceptTermsCheckbox';
import { FinalValue } from './FinalValue';
import { getCartSummaryInfo } from '../../../../utilities/selectors';
import { formatPriceToString, prop } from '../../../../utilities';
import { getZlataPriadkaCartSummaryInfo } from '../../selectors';
import { SvgIcon, IconType } from '../../General/SvgIcon';
import { DeliveryValue } from './DeliveryValue';

interface Props {
  handleStepChange: (step: number) => void;
  data: any;
  handleAcceptTermsChange: (e: any) => void;
  isFetching: boolean;
  freeDel: number | null;
}

function ThirdStep({
  handleStepChange,
  data,
  handleAcceptTermsChange,
  isFetching,
  freeDel,
}: Props) {
  const {
    totalPrice,
    totalPriceWithoutVat,
    vatTotal,
    currency,
    deliveryPrice,
    paymentPrice,
    totalItemsPrice,
    totalItemsPriceWithoutVat,
    vatItems,
  } = getCartSummaryInfo(data);

  const termsAccept = prop(data, 'terms_accept');
  const canOrder =
    termsAccept && prop(data, 'delivery') && prop(data, 'payment');
  const { giftcards } = data;

  const currentGiftCard =
    giftcards && giftcards.length > 0
      ? prop(giftcards, '0.card_number', '')
      : '';

  const showGiftcardItem = giftcards && giftcards.length > 0;

  return (
    <>
      <DesktopFields>
        <SummaryValue
          title={__('Cena tovaru bez DPH')}
          value={formatPriceToString(totalItemsPriceWithoutVat, currency)}
        />
        <SummaryValue
          title={__('DPH')}
          value={formatPriceToString(vatItems, currency)}
        />
        {showGiftcardItem && prop(giftcards, '0.price', 0) > 0 && (
          <SummaryValue
            title={`${__('Zľavový kupón')} ${currentGiftCard}`}
            value={`-${prop(giftcards, '0.price', 0)} ${currency}`}
          />
        )}
        <SummaryValue
          borderBottom={false}
          title={__('Platba s DPH')}
          value={formatPriceToString(paymentPrice, currency)}
        />
        <DeliveryValue
          borderBottom={false}
          title={__('Spôsob dopravy')}
          value={formatPriceToString(deliveryPrice, currency)}
        />
        <FinalValue
          title={__('Celkom k úhrade s DPH')}
          value={formatPriceToString(totalPrice, currency)}
        />
      </DesktopFields>
      <MobileFields>
        <SummaryValue
          title={__('Cena tovaru bez DPH')}
          value={formatPriceToString(totalPriceWithoutVat, currency)}
        />
        <SummaryValue
          title={__('DPH')}
          value={formatPriceToString(vatTotal, currency)}
        />
        {showGiftcardItem && prop(giftcards, '0.price', 0) > 0 && (
          <SummaryValue
            title={`${__('Zľavový kupón')} ${currentGiftCard}`}
            value={`-${prop(giftcards, '0.price', 0)} ${currency}`}
          />
        )}
        <SummaryValue
          borderBottom={false}
          title={__('Platba s DPH')}
          value={formatPriceToString(paymentPrice, currency)}
        />
        <FinalValue
          borderBottom={false}
          title={__('Spôsob dopravy')}
          value={formatPriceToString(deliveryPrice, currency)}
        />
        <FinalValue
          title={__('Celkom k úhrade s DPH')}
          value={formatPriceToString(totalPrice, currency)}
        />
      </MobileFields>

      <AcceptTermsCheckbox
        name="acceptTerms"
        onClick={handleAcceptTermsChange}
        selected={termsAccept}
      />
      <ButtonsWrapper>
        <NextButton
          disabled={!canOrder || isFetching}
          // tslint:disable-next-line:jsx-no-lambda
          onClick={() => handleStepChange(4)}
        >
          {isFetching ? (
            <FlexRowCenter>
              <Loader className="marginLeft" small={true} />
            </FlexRowCenter>
          ) : (
            __('Objednať')
          )}
        </NextButton>
        <Text>{__('s povinnosťou platby')}</Text>
        <BackButtonWrapper>
          <SvgIcon
            width={16}
            height={16}
            viewBox={'0 0 16 16'}
            icon={IconType.leftArrow}
            fill={'black'}
            cursor={'pointer'}
            marginRight={8}
          />
          <BackButton
            // tslint:disable-next-line:jsx-no-lambda
            onClick={() => handleStepChange(2)}
          >
            {__('Späť')}
          </BackButton>
        </BackButtonWrapper>
      </ButtonsWrapper>
    </>
  );
}

export const Loader = styled.div<{ className?: string; small?: boolean }>`
  width: ${({ small }) => (small ? rem(20) : rem(50))};
  height: ${({ small }) => (small ? rem(20) : rem(50))};
  border: ${({ small }) =>
    small ? `${rem(4)} solid #f9f9f9` : `${rem(10)} solid #f9f9f9`};
  border-top: ${({ small, theme }) =>
    small
      ? `${rem(4)} solid ${theme.blogColors.primary}`
      : `${rem(10)} solid ${theme.blogColors.primary}`};
  margin: 0;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const NextButton = styled(ButtonSecondary)`
  width: 100%;
  max-width: ${rem(328)};
  margin: 0;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.secondaryOnHover};
    outline: none;
  }
`;

const BackButton = styled(Link)`
  display: inline-block;
  color: ${({ theme }) => theme.colors.textPrimary};
  border: none;
  margin-bottom: 0;
  font-weight: 500;
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  text-decoration: underline;
`;

const BackButtonWrapper = styled.div`
  margin-top: ${rem(31)};
  display: inline-block;
`;

const ButtonsWrapper = styled(FlexColCenterHorizontal)``;

const DesktopFields = styled(FlexCol)`
  ${({ theme }) => theme.mediab.l925`
    display: none;
  `}
`;

const MobileFields = styled(FlexCol)`
  display: none;
  margin-bottom: ${rem(16)};

  ${({ theme }) => theme.mediab.l925`
    display: flex;
  `}
`;

const Text = styled.p`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  font-family: ${({ theme }) => theme.font.primary};
  color: ${({ theme }) => theme.colors.secondary};
`;

export { ThirdStep };
